(() => {
  const config = window.NCTurnstileConfig;
  const formEntries = Array.isArray(config?.forms) ? config.forms : [];
  const leadEndpoint = config?.leadEndpoint || config?.verifyEndpoint;

  if (!config?.siteKey || !leadEndpoint || formEntries.length === 0) {
    return;
  }

  const states = new WeakMap();
  const buttonLabels = new WeakMap();
  const turnstileApiUrl = config.apiUrl || 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const submittingLabel = config.submittingLabel || (window.NCI18n && window.NCI18n.form && window.NCI18n.form.submitting) || 'Envoi...';
  let turnstileScriptPromise = null;

  const waitForTurnstileReady = () =>
    new Promise((resolve, reject) => {
      const startedAt = Date.now();

      const check = () => {
        if (window.turnstile?.render && window.turnstile?.execute) {
          resolve(window.turnstile);
          return;
        }

        if (Date.now() - startedAt > 5000) {
          reject(new Error('Turnstile unavailable'));
          return;
        }

        window.setTimeout(check, 50);
      };

      check();
    });

  const loadTurnstileScript = () => {
    if (window.turnstile?.render && window.turnstile?.execute) {
      return Promise.resolve(window.turnstile);
    }

    if (turnstileScriptPromise) {
      return turnstileScriptPromise.then(waitForTurnstileReady);
    }

    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = turnstileApiUrl;
      script.async = true;
      script.defer = true;
      script.dataset.ncTurnstileApi = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error('Turnstile API failed to load')), { once: true });
      document.head.appendChild(script);
    });

    return turnstileScriptPromise.then(waitForTurnstileReady);
  };

  const getSubmitButton = (form, submitter) => {
    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
      return submitter;
    }

    return form.querySelector('button[type="submit"], input[type="submit"]');
  };

  const getButtonLabel = (button) => {
    if (!button) return '';
    if (button instanceof HTMLInputElement) return button.value;
    const textNode = button.querySelector('.nc-vf__submit-text, .nc-btn__label');
    if (textNode) return textNode.textContent;

    return Array.from(button.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join('')
      .trim();
  };

  const setButtonLabel = (button, label) => {
    if (!button || !label) return;
    if (button instanceof HTMLInputElement) {
      button.value = label;
      return;
    }

    const textNode = button.querySelector('.nc-vf__submit-text, .nc-btn__label');
    if (textNode) {
      textNode.textContent = label;
      return;
    }

    const directTextNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (directTextNode) {
      directTextNode.textContent = ` ${label} `;
      return;
    }

    button.appendChild(document.createTextNode(label));
  };

  const rememberButtonLabel = (button) => {
    if (!button || buttonLabels.has(button)) return;
    buttonLabels.set(button, getButtonLabel(button));
  };

  const setSpinnerVisible = (button, visible) => {
    if (!button) return;
    const spinner = button.querySelector('.spinner, .nc-vf__submit-loader, .nc-btn__spinner');
    if (!spinner) return;

    if ('hidden' in spinner) {
      spinner.hidden = !visible;
    }

    spinner.style.display = visible ? 'block' : '';
  };

  const setBusy = (button, busy) => {
    if (!button) return;
    const originalLabel = buttonLabels.get(button) || getButtonLabel(button);

    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    setSpinnerVisible(button, busy);
    setButtonLabel(button, busy ? submittingLabel : originalLabel);

    if (!busy) {
      button.removeAttribute('aria-busy');
    }
  };

  const getErrorElement = (form, entry) => {
    if (entry.errorSelector) {
      return document.querySelector(entry.errorSelector);
    }

    return form.querySelector('[role="alert"]');
  };

  const showError = (form, entry, message) => {
    const errorEl = getErrorElement(form, entry);
    if (!errorEl) return;

    const _TF = (window.NCI18n && window.NCI18n.form) || {};
    errorEl.textContent = message || entry.errorMessage || _TF.err_send || "Impossible d'envoyer la demande pour le moment. Veuillez réessayer.";
    errorEl.hidden = false;
  };

  const hideError = (form, entry) => {
    const errorEl = getErrorElement(form, entry);
    if (errorEl) {
      errorEl.hidden = true;
    }
  };

  const ensureWidget = async (form) => {
    let state = states.get(form);
    if (state?.widgetId) return state;

    const turnstile = await loadTurnstileScript();
    let widgetEl = form.querySelector('[data-nc-turnstile-widget]');

    if (!widgetEl) {
      widgetEl = document.createElement('div');
      widgetEl.dataset.ncTurnstileWidget = '';
      widgetEl.style.position = 'absolute';
      widgetEl.style.inlineSize = '1px';
      widgetEl.style.blockSize = '1px';
      widgetEl.style.overflow = 'hidden';
      widgetEl.style.clip = 'rect(0 0 0 0)';
      form.appendChild(widgetEl);
    }

    state = {
      widgetId: null,
      resolveToken: null,
      rejectToken: null,
      submitController: null,
      submitting: false,
    };

    state.widgetId = turnstile.render(widgetEl, {
      sitekey: config.siteKey,
      size: 'invisible',
      execution: 'execute',
      callback(token) {
        if (state.resolveToken) {
          state.resolveToken(token);
          state.resolveToken = null;
          state.rejectToken = null;
        }
      },
      'error-callback'() {
        if (state.rejectToken) {
          state.rejectToken(new Error('Turnstile challenge failed'));
          state.resolveToken = null;
          state.rejectToken = null;
        }
      },
      'expired-callback'() {
        if (state.rejectToken) {
          state.rejectToken(new Error('Turnstile token expired'));
          state.resolveToken = null;
          state.rejectToken = null;
        }
      },
    });

    states.set(form, state);
    return state;
  };

  const executeChallenge = async (form) => {
    const turnstile = await loadTurnstileScript();
    const state = await ensureWidget(form);

    return new Promise((resolve, reject) => {
      state.resolveToken = resolve;
      state.rejectToken = reject;
      turnstile.execute(state.widgetId);
    });
  };

  const normalizeFieldName = (name) => {
    const match = String(name || '').match(/^contact\[(.+)]$/);
    return match ? match[1] : String(name || '');
  };

  const assignField = (fields, name, value) => {
    if (!name) return;

    const normalizedName = normalizeFieldName(name);
    const normalizedValue = String(value || '').trim();
    if (Object.prototype.hasOwnProperty.call(fields, normalizedName)) {
      fields[normalizedName] = `${fields[normalizedName]}, ${normalizedValue}`;
      return;
    }

    fields[normalizedName] = normalizedValue;
  };

  const collectFields = (form) => {
    const fields = {};
    const formData = new FormData(form);

    for (const [name, value] of formData.entries()) {
      if (value instanceof File) continue;
      if (name === 'cf-turnstile-response' || name === 'g-recaptcha-response') continue;
      assignField(fields, name, value);
    }

    return fields;
  };

  const findField = (form, name) => form.elements.namedItem(name) || form.elements.namedItem(`contact[${name}]`);

  const ensureHoneypot = (form) => {
    if (form.querySelector('[name="nc_honeypot"]')) return;

    const honeypot = document.createElement('input');
    honeypot.type = 'text';
    honeypot.name = 'nc_honeypot';
    honeypot.tabIndex = -1;
    honeypot.autocomplete = 'off';
    honeypot.setAttribute('aria-hidden', 'true');
    honeypot.hidden = true;
    form.prepend(honeypot);
  };

  const applyClientRequiredFields = (form, entry) => {
    if (!Array.isArray(entry.requiredFields)) return;

    for (const name of entry.requiredFields) {
      const field = findField(form, name);
      if (!field) continue;
      field.required = true;
      field.setAttribute('aria-required', 'true');
    }
  };

  const submitLead = async (form, entry, token) => {
    const state = await ensureWidget(form);
    state.submitController?.abort();
    state.submitController = new AbortController();

    const response = await fetch(leadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        formId: entry.formId || form.id || '',
        fields: collectFields(form),
        pageUrl: window.location.href,
        referrer: document.referrer || '',
      }),
      signal: state.submitController.signal,
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.success) {
      const error = payload?.error || 'lead_submit_failed';
      throw new Error(error);
    }
  };

  const resetTurnstile = (form) => {
    const state = states.get(form);
    const turnstile = window.turnstile;

    if (state?.widgetId && turnstile?.reset) {
      turnstile.reset(state.widgetId);
    }
  };

  const hideConfiguredElements = (entry) => {
    const selectors = Array.isArray(entry.hideSelectors) ? entry.hideSelectors : [];
    for (const selector of selectors) {
      for (const element of document.querySelectorAll(selector)) {
        element.hidden = true;
        element.style.display = 'none';
      }
    }
  };

  const showSuccessElement = (entry) => {
    if (!entry.successSelector) return false;

    const successEl = document.querySelector(entry.successSelector);
    if (!successEl) return false;

    successEl.hidden = false;
    if (entry.successDisplay) {
      successEl.style.display = entry.successDisplay;
    }

    if (entry.successClass) {
      successEl.classList.add(entry.successClass);
      if (entry.successDuration) {
        window.setTimeout(() => successEl.classList.remove(entry.successClass), entry.successDuration);
      }
    }

    successEl.focus?.();
    return true;
  };

  const closeDialog = (entry) => {
    if (!entry.closeDialogSelector) return;

    const dialog = document.querySelector(entry.closeDialogSelector);
    if (dialog instanceof HTMLDialogElement) {
      if (window.NCA11y?.closeDialog) {
        window.NCA11y.closeDialog(dialog);
        return;
      }

      dialog.close();
    }
  };

  const insertFallbackSuccess = (form, entry) => {
    let successEl = form.parentElement?.querySelector('[data-nc-lead-success]');

    if (!successEl) {
      successEl = document.createElement('div');
      successEl.dataset.ncLeadSuccess = 'true';
      successEl.className = 'nc-form-success';
      successEl.setAttribute('role', 'status');
      successEl.setAttribute('tabindex', '-1');
      form.insertAdjacentElement('afterend', successEl);
    }

    const _TF2 = (window.NCI18n && window.NCI18n.form) || {};
    successEl.textContent = entry.successMessage || _TF2.success_generic || 'Votre demande a bien été envoyée.';
    successEl.hidden = false;
    successEl.focus();
  };

  const showSuccess = (form, entry) => {
    hideConfiguredElements(entry);
    form.hidden = Boolean(entry.hideForm);
    if (entry.hideForm) {
      form.style.display = 'none';
    }

    closeDialog(entry);

    const hasSuccess = showSuccessElement(entry);
    if (!hasSuccess && !entry.successSelector) {
      insertFallbackSuccess(form, entry);
    }

    form.dispatchEvent(
      new CustomEvent('nc:lead-success', {
        detail: { formId: entry.formId || form.id || '' },
        bubbles: true,
      })
    );
  };

  const resetAfterError = (form, entry, button) => {
    resetTurnstile(form);
    setBusy(button, false);
    showError(form, entry);
  };

  const protectSubmit = async (event, form, entry) => {
    if (event.defaultPrevented) return;

    event.preventDefault();
    const state = await ensureWidget(form);
    if (state.submitting) {
      return;
    }

    const button = getSubmitButton(form, event.submitter);
    rememberButtonLabel(button);
    hideError(form, entry);
    setBusy(button, true);
    state.submitting = true;

    try {
      const token = await executeChallenge(form);
      await submitLead(form, entry, token);
      showSuccess(form, entry);
      setBusy(button, false);
    } catch (error) {
      if (error.name !== 'AbortError') {
        resetAfterError(form, entry, button);
      }
    } finally {
      state.submitting = false;
    }
  };

  const initForm = (form, entry) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.ncLeadTurnstile === 'ready') return;

    form.dataset.ncLeadTurnstile = 'ready';
    ensureHoneypot(form);
    applyClientRequiredFields(form, entry);
    rememberButtonLabel(getSubmitButton(form));
    form.addEventListener('submit', (event) => {
      protectSubmit(event, form, entry);
    });
  };

  const init = () => {
    for (const entry of formEntries) {
      const forms = document.querySelectorAll(entry.selector);

      for (const form of forms) {
        initForm(form, entry);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    return;
  }

  init();
})();
